import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { apiFetch } from '../lib/apiFetch';
import {
  pageStyle,
  headingStyle,
  mutedTextStyle,
  errorBoxStyle,
  retryBtnStyle,
  cardStyle,
  cardStaticStyle,
  cardHeaderRowStyle,
  cardTitleStyle,
  cardHintStyle,
  leagueFormatTagStyle,
  badgeStyle,
  statusColor,
  adminTabRowStyle,
  adminTabBtnStyle,
  adminTabBtnActiveStyle,
  adminSectionHeadStyle,
  adminToolbarStyle,
  adminPrimaryBtnStyle,
  adminSecondaryBtnStyle,
  adminDangerBtnStyle,
  adminFormGroupStyle,
  adminLabelStyle,
  adminInputStyle,
  adminSelectStyle,
  adminFormRowStyle,
  adminModalOverlayStyle,
  adminModalStyle,
  adminModalTitleStyle,
  adminModalFootStyle,
  adminEmptyStyle,
  adminChipStyle,
} from '../App.styles';

// ─── Types ────────────────────────────────────────────────────────────────────

interface League {
  id: string; name: string; format: string;
}
interface Season {
  id: string; leagueId: string; startDate: string; endDate: string; status: string;
  league?: { id: string; name: string };
}
interface Team {
  id: string; name: string; seasonId: string; divisionId: string;
  division?: { id: string; name: string };
  players?: Player[];
}
interface Player {
  id: string; userId: string; teamId: string; seasonId: string;
  user?: { id: string; name: string; email: string };
  team?: { id: string; name: string };
}
interface Division {
  id: string; name: string; format: string;
}

type Tab = 'leagues' | 'seasons' | 'teams' | 'players';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmt(iso: string): string {
  try { return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(iso)); }
  catch { return iso; }
}

// ─── Modal wrapper ────────────────────────────────────────────────────────────

function Modal({ title, onClose, children, footer }: {
  title: string; onClose: () => void;
  children: React.ReactNode; footer?: React.ReactNode;
}) {
  return (
    <div style={adminModalOverlayStyle} onClick={onClose}>
      <div style={adminModalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={adminModalTitleStyle}>{title}</h3>
        {children}
        {footer && <div style={adminModalFootStyle}>{footer}</div>}
      </div>
    </div>
  );
}

// ─── Leagues tab ─────────────────────────────────────────────────────────────

function LeaguesTab({ slug, token }: { slug: string; token: string | null }) {
  const [leagues, setLeagues] = useState<League[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', format: 'TEAMS' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await apiFetch('/api/v1/leagues', { slug, token });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      setLeagues(await res.json());
    } catch (e: unknown) { setError(e instanceof Error ? e.message : 'Failed'); }
    finally { setLoading(false); }
  }, [slug, token]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    setSaving(true); setSaveError(null);
    try {
      const res = await apiFetch('/api/v1/leagues', {
        method: 'POST', slug, token,
        body: JSON.stringify({ name: form.name, format: form.format, scoringRules: {} }),
      });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error((b as {message?:string}).message || 'HTTP ' + res.status); }
      setCreating(false); setForm({ name: '', format: 'TEAMS' }); await load();
    } catch (e: unknown) { setSaveError(e instanceof Error ? e.message : 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <section>
      <div style={adminToolbarStyle}>
        <h2 style={adminSectionHeadStyle}>Leagues</h2>
        <button style={adminPrimaryBtnStyle} onClick={() => setCreating(true)}>+ New League</button>
      </div>
      {loading && <p style={mutedTextStyle}>Loading…</p>}
      {error && <div style={errorBoxStyle}>{error} <button style={retryBtnStyle} onClick={load}>Retry</button></div>}
      {!loading && !error && leagues.length === 0 && <p style={adminEmptyStyle}>No leagues yet.</p>}
      {leagues.map(l => (
        <div key={l.id} style={cardStaticStyle}>
          <div style={cardHeaderRowStyle}>
            <span style={cardTitleStyle}>{l.name}</span>
            <span style={leagueFormatTagStyle}>{l.format.replace(/_/g, ' ')}</span>
          </div>
          <p style={cardHintStyle}>{l.id}</p>
        </div>
      ))}
      {creating && (
        <Modal title="New League" onClose={() => setCreating(false)} footer={
          <>
            <button style={adminPrimaryBtnStyle} onClick={handleCreate} disabled={saving || !form.name.trim()}>
              {saving ? 'Saving…' : 'Create'}
            </button>
            <button style={adminSecondaryBtnStyle} onClick={() => setCreating(false)} disabled={saving}>Cancel</button>
            {saveError && <span style={{ color: '#dc2626', fontSize: 13 }}>{saveError}</span>}
          </>
        }>
          <div style={adminFormGroupStyle}>
            <label style={adminLabelStyle}>League Name
              <input style={adminInputStyle} value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Spring Singles" />
            </label>
            <label style={adminLabelStyle}>Format
              <select style={adminSelectStyle} value={form.format}
                onChange={e => setForm(f => ({ ...f, format: e.target.value }))}>
                <option value="TEAMS">Teams</option>
                <option value="SINGLES">Singles</option>
              </select>
            </label>
          </div>
        </Modal>
      )}
    </section>
  );
}

// ─── Seasons tab ─────────────────────────────────────────────────────────────

function SeasonsTab({ slug, token }: { slug: string; token: string | null }) {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ leagueId: '', startDate: '', endDate: '', status: 'DRAFT' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [sRes, lRes] = await Promise.all([
        apiFetch('/api/v1/seasons', { slug, token }),
        apiFetch('/api/v1/leagues', { slug, token }),
      ]);
      if (!sRes.ok) throw new Error('Seasons HTTP ' + sRes.status);
      if (!lRes.ok) throw new Error('Leagues HTTP ' + lRes.status);
      const [s, l] = await Promise.all([sRes.json(), lRes.json()]);
      setSeasons(s); setLeagues(l);
      if (l.length > 0 && !form.leagueId) setForm(f => ({ ...f, leagueId: l[0].id }));
    } catch (e: unknown) { setError(e instanceof Error ? e.message : 'Failed'); }
    finally { setLoading(false); }
  }, [slug, token]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    setSaving(true); setSaveError(null);
    try {
      const res = await apiFetch('/api/v1/seasons', {
        method: 'POST', slug, token,
        body: JSON.stringify(form),
      });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error((b as {message?:string}).message || 'HTTP ' + res.status); }
      setCreating(false); setForm(f => ({ ...f, startDate: '', endDate: '', status: 'DRAFT' })); await load();
    } catch (e: unknown) { setSaveError(e instanceof Error ? e.message : 'Failed'); }
    finally { setSaving(false); }
  };

  const handleStatusChange = async (id: string, status: string) => {
    try {
      await apiFetch('/api/v1/seasons/' + id, {
        method: 'PUT', slug, token,
        body: JSON.stringify({ status }),
      });
      await load();
    } catch { /* ignore */ }
  };

  return (
    <section>
      <div style={adminToolbarStyle}>
        <h2 style={adminSectionHeadStyle}>Seasons</h2>
        <button style={adminPrimaryBtnStyle} onClick={() => setCreating(true)}>+ New Season</button>
      </div>
      {loading && <p style={mutedTextStyle}>Loading…</p>}
      {error && <div style={errorBoxStyle}>{error} <button style={retryBtnStyle} onClick={load}>Retry</button></div>}
      {!loading && !error && seasons.length === 0 && <p style={adminEmptyStyle}>No seasons yet.</p>}
      {seasons.map(s => (
        <div key={s.id} style={cardStaticStyle}>
          <div style={cardHeaderRowStyle}>
            <span style={cardTitleStyle}>{s.league?.name ?? s.leagueId}</span>
            <span style={badgeStyle(statusColor[s.status] ?? '#6b7280')}>{s.status}</span>
          </div>
          <p style={cardHintStyle}>{fmt(s.startDate)} → {fmt(s.endDate)}</p>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            {(['DRAFT', 'ACTIVE', 'ARCHIVED'] as const).filter(st => st !== s.status).map(st => (
              <button key={st} style={adminSecondaryBtnStyle}
                onClick={() => handleStatusChange(s.id, st)}>
                → {st}
              </button>
            ))}
          </div>
        </div>
      ))}
      {creating && (
        <Modal title="New Season" onClose={() => setCreating(false)} footer={
          <>
            <button style={adminPrimaryBtnStyle} onClick={handleCreate}
              disabled={saving || !form.leagueId || !form.startDate || !form.endDate}>
              {saving ? 'Saving…' : 'Create'}
            </button>
            <button style={adminSecondaryBtnStyle} onClick={() => setCreating(false)} disabled={saving}>Cancel</button>
            {saveError && <span style={{ color: '#dc2626', fontSize: 13 }}>{saveError}</span>}
          </>
        }>
          <div style={adminFormGroupStyle}>
            <label style={adminLabelStyle}>League
              <select style={adminSelectStyle} value={form.leagueId}
                onChange={e => setForm(f => ({ ...f, leagueId: e.target.value }))}>
                <option value="">— select —</option>
                {leagues.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </label>
            <div style={adminFormRowStyle}>
              <label style={adminLabelStyle}>Start Date
                <input type="date" style={adminInputStyle} value={form.startDate}
                  onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} />
              </label>
              <label style={adminLabelStyle}>End Date
                <input type="date" style={adminInputStyle} value={form.endDate}
                  onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} />
              </label>
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
}

// ─── Teams tab ────────────────────────────────────────────────────────────────

function TeamsTab({ slug, token }: { slug: string; token: string | null }) {
  const [teams, setTeams] = useState<Team[]>([]);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [filterSeason, setFilterSeason] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [form, setForm] = useState({ name: '', seasonId: '', divisionId: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const loadSeasons = useCallback(async () => {
    const res = await apiFetch('/api/v1/seasons', { slug, token });
    if (res.ok) {
      const s: Season[] = await res.json();
      setSeasons(s);
      if (s.length > 0 && !filterSeason) setFilterSeason(s[0].id);
    }
  }, [slug, token]);

  const loadTeams = useCallback(async (seasonId: string) => {
    if (!seasonId) return;
    setLoading(true); setError(null);
    try {
      const res = await apiFetch('/api/v1/teams?seasonId=' + seasonId, { slug, token });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      setTeams(await res.json());
    } catch (e: unknown) { setError(e instanceof Error ? e.message : 'Failed'); }
    finally { setLoading(false); }
  }, [slug, token]);

  useEffect(() => { loadSeasons(); }, [loadSeasons]);
  useEffect(() => { if (filterSeason) loadTeams(filterSeason); }, [filterSeason, loadTeams]);

  const openCreate = useCallback(async () => {
    const sid = filterSeason || seasons[0]?.id || '';
    setForm({ name: '', seasonId: sid, divisionId: '' });
    if (sid) {
      const res = await apiFetch('/api/v1/seasons/' + sid, { slug, token });
      if (res.ok) {
        const s: Season & { divisions?: Division[] } = await res.json();
        setDivisions(s.divisions ?? []);
      }
    }
    setCreating(true);
  }, [filterSeason, seasons, slug, token]);

  const handleCreate = async () => {
    setSaving(true); setSaveError(null);
    try {
      const res = await apiFetch('/api/v1/teams', {
        method: 'POST', slug, token,
        body: JSON.stringify(form),
      });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error((b as {message?:string}).message || 'HTTP ' + res.status); }
      setCreating(false); await loadTeams(filterSeason);
    } catch (e: unknown) { setSaveError(e instanceof Error ? e.message : 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this team? This cannot be undone.')) return;
    try {
      await apiFetch('/api/v1/teams/' + id, { method: 'DELETE', slug, token });
      await loadTeams(filterSeason);
    } catch { /* ignore */ }
  };

  return (
    <section>
      <div style={adminToolbarStyle}>
        <h2 style={adminSectionHeadStyle}>Teams</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select style={adminSelectStyle} value={filterSeason}
            onChange={e => setFilterSeason(e.target.value)}>
            <option value="">— pick season —</option>
            {seasons.map(s => (
              <option key={s.id} value={s.id}>
                {s.league?.name ?? s.leagueId} ({s.status})
              </option>
            ))}
          </select>
          <button style={adminPrimaryBtnStyle} onClick={openCreate} disabled={!filterSeason}>+ New Team</button>
        </div>
      </div>
      {loading && <p style={mutedTextStyle}>Loading…</p>}
      {error && <div style={errorBoxStyle}>{error} <button style={retryBtnStyle} onClick={() => loadTeams(filterSeason)}>Retry</button></div>}
      {!loading && !error && teams.length === 0 && <p style={adminEmptyStyle}>No teams for this season.</p>}
      {teams.map(t => (
        <div key={t.id} style={cardStaticStyle}>
          <div style={cardHeaderRowStyle}>
            <span style={cardTitleStyle}>{t.name}</span>
            {t.division && <span style={leagueFormatTagStyle}>{t.division.name}</span>}
            <button style={adminDangerBtnStyle} onClick={() => handleDelete(t.id)}>Delete</button>
          </div>
          {t.players && t.players.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {t.players.map(p => (
                <span key={p.id} style={adminChipStyle}>{p.user?.name ?? p.userId}</span>
              ))}
            </div>
          )}
        </div>
      ))}
      {creating && (
        <Modal title="New Team" onClose={() => setCreating(false)} footer={
          <>
            <button style={adminPrimaryBtnStyle} onClick={handleCreate}
              disabled={saving || !form.name.trim() || !form.divisionId}>
              {saving ? 'Saving…' : 'Create'}
            </button>
            <button style={adminSecondaryBtnStyle} onClick={() => setCreating(false)} disabled={saving}>Cancel</button>
            {saveError && <span style={{ color: '#dc2626', fontSize: 13 }}>{saveError}</span>}
          </>
        }>
          <div style={adminFormGroupStyle}>
            <label style={adminLabelStyle}>Team Name
              <input style={adminInputStyle} value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Pink Flamingos" />
            </label>
            <label style={adminLabelStyle}>Division
              <select style={adminSelectStyle} value={form.divisionId}
                onChange={e => setForm(f => ({ ...f, divisionId: e.target.value }))}>
                <option value="">— select division —</option>
                {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </label>
          </div>
        </Modal>
      )}
    </section>
  );
}

// ─── Players tab ──────────────────────────────────────────────────────────────

function PlayersTab({ slug, token }: { slug: string; token: string | null }) {
  const [players, setPlayers] = useState<Player[]>([]);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [filterSeason, setFilterSeason] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSeasons = useCallback(async () => {
    const res = await apiFetch('/api/v1/seasons', { slug, token });
    if (res.ok) {
      const s: Season[] = await res.json();
      setSeasons(s);
      if (s.length > 0 && !filterSeason) setFilterSeason(s[0].id);
    }
  }, [slug, token]);

  const loadPlayers = useCallback(async (seasonId: string) => {
    if (!seasonId) return;
    setLoading(true); setError(null);
    try {
      const res = await apiFetch('/api/v1/players?seasonId=' + seasonId, { slug, token });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      setPlayers(await res.json());
    } catch (e: unknown) { setError(e instanceof Error ? e.message : 'Failed'); }
    finally { setLoading(false); }
  }, [slug, token]);

  useEffect(() => { loadSeasons(); }, [loadSeasons]);
  useEffect(() => { if (filterSeason) loadPlayers(filterSeason); }, [filterSeason, loadPlayers]);

  const handleRemove = async (id: string) => {
    if (!confirm('Remove this player from the season?')) return;
    try {
      await apiFetch('/api/v1/players/' + id, { method: 'DELETE', slug, token });
      await loadPlayers(filterSeason);
    } catch { /* ignore */ }
  };

  return (
    <section>
      <div style={adminToolbarStyle}>
        <h2 style={adminSectionHeadStyle}>Players</h2>
        <select style={adminSelectStyle} value={filterSeason}
          onChange={e => setFilterSeason(e.target.value)}>
          <option value="">— pick season —</option>
          {seasons.map(s => (
            <option key={s.id} value={s.id}>
              {s.league?.name ?? s.leagueId} ({s.status})
            </option>
          ))}
        </select>
      </div>
      {loading && <p style={mutedTextStyle}>Loading…</p>}
      {error && <div style={errorBoxStyle}>{error} <button style={retryBtnStyle} onClick={() => loadPlayers(filterSeason)}>Retry</button></div>}
      {!loading && !error && players.length === 0 && <p style={adminEmptyStyle}>No players for this season.</p>}
      {players.map(p => (
        <div key={p.id} style={{ ...cardStaticStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={cardTitleStyle}>{p.user?.name ?? p.userId}</div>
            <div style={cardHintStyle}>{p.user?.email} · {p.team?.name ?? p.teamId}</div>
          </div>
          <button style={adminDangerBtnStyle} onClick={() => handleRemove(p.id)}>Remove</button>
        </div>
      ))}
    </section>
  );
}

// ─── Main AdminPage ───────────────────────────────────────────────────────────

export function AdminPage(): React.JSX.Element {
  const { slug } = useTenant();
  const { user, token } = useAuth();
  const [tab, setTab] = useState<Tab>('leagues');

  const isAdmin = !!user?.roles?.some((r: string) =>
    ['ORG_ADMIN', 'LEAGUE_ADMIN', 'SUPER_ADMIN'].includes(r)
  );

  if (!user) {
    return <main style={pageStyle}><p style={mutedTextStyle}>Please sign in to access the admin dashboard.</p></main>;
  }
  if (!isAdmin) {
    return <main style={pageStyle}><p style={mutedTextStyle}>You do not have permission to view this page.</p></main>;
  }
  if (!slug) {
    return <main style={pageStyle}><p style={mutedTextStyle}>Admin dashboard is only available within a club.</p></main>;
  }

  const tabBtn = (label: string, key: Tab) => (
    <button
      key={key}
      style={tab === key ? adminTabBtnActiveStyle : adminTabBtnStyle}
      onClick={() => setTab(key)}
    >
      {label}
    </button>
  );

  return (
    <main style={pageStyle}>
      <h1 style={headingStyle}>Admin Dashboard</h1>
      <nav style={adminTabRowStyle}>
        {tabBtn('Leagues', 'leagues')}
        {tabBtn('Seasons', 'seasons')}
        {tabBtn('Teams', 'teams')}
        {tabBtn('Players', 'players')}
      </nav>
      <div style={{ marginTop: '1.5rem' }}>
        {tab === 'leagues' && <LeaguesTab slug={slug} token={token} />}
        {tab === 'seasons' && <SeasonsTab slug={slug} token={token} />}
        {tab === 'teams' && <TeamsTab slug={slug} token={token} />}
        {tab === 'players' && <PlayersTab slug={slug} token={token} />}
      </div>
    </main>
  );
}
